import { useContext, useEffect } from 'react';
import { MdNavigateBefore } from 'react-icons/md';
import { IconButton, Spinner } from '@ds';
import { CaseLogistics } from '@lp/features/CaseLogistics';
import { StreamChatShareProvider } from '@features/components/Chat/contexts/ChatShareContext';
import { Link, Navigate, useParams } from 'react-router-dom';
import { AuthContext } from '@lp/core/auth';
import CaseOverview from '@lp/features/CaseOverview';
import { useCaseTabs } from '@lp/core/hooks/useCaseTabs';
import { useCaseDetailsViewPermission } from '@lp/core/hooks/useCaseDetailsViewPermission';
import { useCaseEventsSubHandler } from '@lp/core/hooks/useCaseEventsSubHandler';
import { getCaseSuffixes, isAllowedStatus } from '@/utils/helpers';
import { CaseStatus } from '@/api/apis/case';
import { useCaseByIdQuery } from '@/queries/case';
import { CaseDetailsPermissions, RouteBuilderPermissions } from '@/permissions';
import { setCaseTag } from '@/sentry';

export const CaseDetails = () => {
  const params = useParams<{ caseId: string }>();
  const { data } = useCaseByIdQuery(params.caseId as string);
  const { addTab, patchTab, tabsList } = useCaseTabs();
  const { checkPermission } = useContext(AuthContext);
  const viewForbidden = useCaseDetailsViewPermission(data);
  const hasLogisticsPanelReadPermission = checkPermission(CaseDetailsPermissions.LogisticsPanelRead);
  const hasViewBuilderPermission = checkPermission(RouteBuilderPermissions.ViewBuilder);

  useCaseEventsSubHandler(params.caseId!, data?.logisticsReference?.id);

  useEffect(() => {
    if (params.caseId) {
      setCaseTag(params.caseId);
    }

    return () => {
      setCaseTag(null);
    };
  }, [params.caseId]);

  useEffect(() => {
    if (data) {
      const {
        caseId,
        donorId,
        organType,
        reAllocationSequenceNumber,
        opoRequest,
        isExpeditedCase,
        laterality,
        duplicateCaseNumber,
      } = data;

      const newTab = {
        id: caseId,
        donorId,
        caseSuffixes: getCaseSuffixes(
          organType,
          reAllocationSequenceNumber,
          opoRequest,
          isExpeditedCase,
          laterality,
          duplicateCaseNumber,
        ),
        organType,
      };

      const existingTab = tabsList.find((tab) => tab.id === data.caseId);

      if (!existingTab) {
        addTab(newTab);
      } else if (existingTab.caseSuffixes !== newTab.caseSuffixes || existingTab.donorId !== newTab.donorId) {
        patchTab(caseId, {
          donorId: newTab.donorId,
          caseSuffixes: newTab.caseSuffixes,
          organType: newTab.organType,
        });
      }
    }
  }, [data, addTab, patchTab, tabsList]);

  if (!data) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <Spinner />
      </div>
    );
  }

  if (viewForbidden) {
    return <Navigate to="/no-access" replace />;
  }

  const isPlannerAllowed = isAllowedStatus({
    statusFrom: CaseStatus.PendingLogistics,
    currentStatus: data.status,
  });

  return (
    <StreamChatShareProvider>
      <div className="h-full flex items-center bg-white overflow-auto">
        <div className="flex h-full w-full relative bg-base-70 shadow-medium rounded-tr-[40px] transition-[max-width] max-w-full">
          {isPlannerAllowed && !!data.routePlanningStatus && (
            <IconButton
              isDisabled={!hasViewBuilderPermission}
              size="xs"
              as={Link}
              variant="solid"
              className="absolute top-10 -right-3"
              to={`/case/${data.caseId}/planner`}
              startContent={<MdNavigateBefore />}
            />
          )}
          <CaseOverview caseId={data.caseId} />
        </div>
        {hasLogisticsPanelReadPermission && <CaseLogistics caseId={data.caseId} />}
      </div>
    </StreamChatShareProvider>
  );
};
